/**
 * BaseMap Component
 *
 * Reusable Google Maps component that can display any markers.
 * Used by the property and conversion detail views (a single marker). The multi-layer map
 * it was also written for (UnifiedMap) was retired on 28 Sep 2026 for components/mapv2.
 *
 * @param {Object} center - Map center { lat, lng }
 * @param {Array} markers - Array of marker objects with { lat, lng, color, shape, label, popup }
 * @param {number} zoom - Map zoom level (1-20)
 * @param {Object} options - Google Maps options
 * @param {Function} onMapLoad - Callback when map is loaded
 * @param {string} height - Map container height (default: "800px")
 * @param {string} width - Map container width (default: "100%")
 */

import React, { useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import PropTypes from "prop-types";

const DEFAULT_CENTER = {
  lat: 52.27,
  lng: -1.87,
};

const DEFAULT_ZOOM = 7;

const DEFAULT_OPTIONS = {
  disableDefaultUI: false,
  zoomControl: true,
  gestureHandling: "greedy",
  mapId: "clozesure-properties",
};

export const BaseMap = ({
  center = DEFAULT_CENTER,
  markers = [],
  zoom = DEFAULT_ZOOM,
  options = DEFAULT_OPTIONS,
  onMapLoad,
  height = "800px",
  width = "100%",
  children,
}) => {
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Memoize mapContainerStyle to prevent GoogleMap from remounting
  const mapContainerStyle = React.useMemo(() => ({ width, height }), [width, height]);

  const onLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (onMapLoad) {
        onMapLoad(map);
      }
    },
    [onMapLoad]
  );

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  if (loadError) {
    return (
      <div
        style={{
          width,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f3f4f6",
          color: "#d32f2f",
        }}
      >
        Error loading map
      </div>
    );
  }

  // Check if Google Maps is loaded
  if (!isLoaded) {
    return (
      <div
        style={{
          width,
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f3f4f6",
          color: "#6b7280",
        }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={zoom}
      options={options}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {children}
    </GoogleMap>
  );
};

BaseMap.propTypes = {
  center: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      color: PropTypes.string,
      shape: PropTypes.string,
      label: PropTypes.string,
      popup: PropTypes.node,
    })
  ),
  zoom: PropTypes.number,
  options: PropTypes.object,
  onMapLoad: PropTypes.func,
  height: PropTypes.string,
  width: PropTypes.string,
  children: PropTypes.node,
};

export default BaseMap;
